import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import Layout from "../Common/Layout";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  requestNotificationPermission,
  showNotification,
} from "./utils/notification";

const MasterClientList = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  

  // 🔑 Store previous client IDs
  const prevClientIdsRef = useRef([]);

  useEffect(() => {
    requestNotificationPermission();

    fetchMasterClients();

    // 🔁 Poll every 10 seconds
    const interval = setInterval(() => {
      fetchMasterClients();
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  const fetchMasterClients = async () => {
    try {
      const token = sessionStorage.getItem("token");
      const response = await axios.get(
        "https://irc.zentelex.com/api/master-client/getclients",
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const newClients = response.data.data || [];

      // 🔔 Detect newly added client
      if (prevClientIdsRef.current.length > 0) {
        const prevIds = new Set(prevClientIdsRef.current);

        const newlyAdded = newClients.find((c) => !prevIds.has(c.id));

        if (newlyAdded && Notification.permission === "granted") {
          showNotification(
            "New Master Client",
            `${newlyAdded.company_name} submitted a request`
          );

          toast.info(`New master client: ${newlyAdded.company_name}`);
        }
      }

      // Save IDs for next comparison
      prevClientIdsRef.current = newClients.map((c) => c.id);

      setClients(newClients);
    } catch (error) {
      console.error("Error fetching master clients:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleView = (client) => {
    setSelectedClient(client);
    setModalOpen(true);
  };

  const handleApprove = async () => {
    try {
      if (!selectedClient?.id) return;

      const token = sessionStorage.getItem("token");

      await axios.put(
        `https://irc.zentelex.com/api/master-client/approve/${selectedClient.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Client approved successfully");

      setModalOpen(false);
      setSelectedClient(null);
      fetchMasterClients();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Approval failed");
    }
  };

  const renderStatusBadge = (status = "PENDING") => {
    const colors = {
      APPROVED: "#28a745",
      REJECTED: "#dc3545",
      PENDING: "#ffc107",
    };

    return (
      <span
        style={{
          padding: "4px 10px",
          borderRadius: "12px",
          fontSize: "12px",
          fontWeight: 600,
          color: "#fff",
          background: colors[status] || "#6c757d",
        }}
      >
        {status}
      </span>
    );
  };

  return (
    <Layout>
      <div className="content-page">
        <div className="content">
          <div style={{ padding: "20px" }}>
            <div style={cardStyle}>
              <div style={headerStyle}>Master Clients List</div>

              <div style={{ padding: "16px" }}>
                {loading ? (
                  <div style={{ textAlign: "center" }}>Loading...</div>
                ) : clients.length === 0 ? (
                  <div style={{ textAlign: "center", color: "#777" }}>
                    No master clients found
                  </div>
                ) : (
                  <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
                    <table style={tableStyle}>
                      <thead>
                        <tr style={{ background: "#f8f9fa" }}>
                          {[
                            "#",
                            "Company Name",
                            "Contact No",
                            "Product",
                            "Quantity",
                            "Quality Grade",
                            "Order Date",
                            "Storage Date",
                            "Address",
                            "Status",
                            "Actions",
                          ].map((h, i) => (
                            <th key={i} style={thStyle}>
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>

                      <tbody>
                        {clients.map((client, index) => (
                          <tr key={client.id}>
                            <td style={tdStyle}>{index + 1}</td>
                            <td style={{ ...tdStyle, fontWeight: 600 }}>
                              {client.company_name}
                            </td>
                            <td style={tdStyle}>{client.contact_number}</td>
                            <td style={tdStyle}>{client.product}</td>
                            <td style={{ ...tdStyle, textAlign: "right" }}>
                              {client.quantity}
                            </td>
                            <td style={tdStyle}>{client.quality_grade}</td>
                            <td style={tdStyle}>{client.order_date}</td>
                            <td style={tdStyle}>
                              {client.storage_tentative_date}
                            </td>
                            <td style={ellipsisStyle}>{client.address}</td>
                            <td style={tdStyle}>
                              {renderStatusBadge(client.status)}
                            </td>
                            <td style={tdStyle}>
                              <button
                                onClick={() => handleView(client)}
                                style={buttonStyle}
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {modalOpen && selectedClient && (
        <div style={modalOverlayStyle}>
          <div style={modalStyle}>
            <h3>Client Details</h3>

            <p>
              <strong>Company:</strong> {selectedClient.company_name}
            </p>
            <p>
              <strong>Contact:</strong> {selectedClient.contact_number}
            </p>
            <p>
              <strong>Product:</strong> {selectedClient.product}
            </p>
            <p>
              <strong>Quantity:</strong> {selectedClient.quantity}
            </p>
            <p>
              <strong>Quality:</strong> {selectedClient.quality_grade}
            </p>
            <p>
              <strong>Order Date:</strong> {selectedClient.order_date}
            </p>
            <p>
              <strong>Storage Date:</strong>{" "}
              {selectedClient.storage_tentative_date}
            </p>
            <p>
              <strong>Address:</strong> {selectedClient.address}
            </p>
            <p>
              <strong>Status:</strong>{" "}
              {renderStatusBadge(selectedClient.status)}
            </p>

            <div style={{ textAlign: "right", marginTop: "20px" }}>
              <button
                onClick={() => setModalOpen(false)}
                style={{ ...buttonStyle, marginRight: "10px" }}
              >
                Close
              </button>

              <button
                onClick={handleApprove}
                disabled={selectedClient.status === "APPROVED"}
                style={{
                  ...buttonStyle,
                  background:
                    selectedClient.status === "APPROVED"
                      ? "#6c757d"
                      : "#007bff",
                  cursor:
                    selectedClient.status === "APPROVED"
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                {selectedClient.status === "APPROVED" ? "Approved" : "Approve"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} />
    </Layout>
  );
};

/* ================= STYLES ================= */

const cardStyle = {
  background: "#fff",
  borderRadius: "6px",
  boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
};

const headerStyle = {
  padding: "12px 16px",
  fontWeight: 600,
  borderBottom: "1px solid #ddd",
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  tableLayout: "fixed",
  fontSize: "14px",
};

const thStyle = {
  padding: "10px",
  border: "1px solid #ddd",
  whiteSpace: "nowrap",
};

const tdStyle = {
  padding: "10px",
  border: "1px solid #ddd",
};

const ellipsisStyle = {
  ...tdStyle,
  maxWidth: "220px",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const buttonStyle = {
  padding: "6px 12px",
  border: "none",
  borderRadius: "4px",
  background: "#007bff",
  color: "#fff",
  cursor: "pointer",
};

const modalOverlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.5)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1000,
};

const modalStyle = {
  background: "#fff",
  padding: "20px",
  borderRadius: "6px",
  width: "420px",
  maxHeight: "80vh",
  overflowY: "auto",
};

export default MasterClientList;
